import axios from "axios";
import { createContext, useContext, useEffect, useState } from "react";
import { userConfirmAction, userError, userSuccess } from "../utils/toastUtils";

const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [adminRole, setAdminRole] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiLoading, setApiLoading] = useState(false);

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem("adminToken");

      if (!token) {
        setLoading(false);
        return;
      }

      await hydrateAdminFromServer();
      setLoading(false);
    };

    init();
  }, []);

  const hydrateAdminFromServer = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) return false;

    setApiLoading(true);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/admin/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const admin = res.data.admin;

      localStorage.setItem("adminData", JSON.stringify(admin));
      localStorage.setItem("adminRole", admin.userType);

      setAdmin(admin);
      setAdminRole(admin.userType);
      setAdminToken(token);

      return true;
    } catch (error) {
      // token invalid OR admin deleted
      silentLogout();
      return false;
    } finally {
      setApiLoading(false);
    }
  };

  const requestAdminOtp = async ({ phoneNumber }) => {
    setApiLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/admin/request-otp`,
        { phoneNumber },
      );

      userSuccess(res.data.message || "OTP sent successfully");

      return {
        success: true,
        expiresIn: res.data.expiresIn,
      };
    } catch (error) {
      userError(error?.response?.data?.message || "OTP Request Failed");
      console.log("OTP Request Failed:", error.message);
      return {
        success: false,
      };
    } finally {
      setApiLoading(false);
    }
  };

  const verifyAdminOtp = async ({ phoneNumber, otp }) => {
    setApiLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/admin/verify-otp`,
        { phoneNumber, otp },
      );

      const { token, admin } = res.data;

      localStorage.setItem("adminToken", token);
      localStorage.setItem("adminData", JSON.stringify(admin));
      localStorage.setItem("adminRole", admin.userType);

      setAdminToken(token);
      setAdmin(admin);
      setAdminRole(admin.userType);

      userSuccess("Logged in successfully");
    } catch (error) {
      userError(error?.response?.data?.message || "OTP Verification Failed");
      console.log("OTP Verification Failed:", error.message);
    } finally {
      setApiLoading(false);
    }
  };

  const resendAdminOtp = async ({ phoneNumber }) => {
    setApiLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/admin/resend-otp`,
        { phoneNumber },
      );

      userSuccess(res.data.message || "OTP resent successfully");

      return {
        success: true,
        expiresIn: res.data.expiresIn,
      };
    } catch (error) {
      userError(error?.response?.data?.message || "Failed to resend OTP");
      console.log("Resend OTP Failed:", error.message);
      return {
        success: false,
      };
    } finally {
      setApiLoading(false);
    }
  };

  const silentLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminData");
    localStorage.removeItem("adminRole");

    setAdmin(null);
    setAdminRole(null);
    setAdminToken(null);
  };

  const logoutAdmin = async () => {
    const role = adminRole === "staff" ? "Staff" : "Admin";

    const confirmed = await userConfirmAction({
      title: `Log out from ${role} Panel?`,
      text: `You’ll be signed out of the ${role.toLowerCase()} dashboard on this device.`,
      confirmText: "Yes, log out",
      cancelText: "Stay logged in",
      icon: "warning",
    });

    if (!confirmed) return false;

    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminData");
    localStorage.removeItem("adminRole");

    setAdminToken(null);
    setAdmin(null);
    setAdminRole(null);

    userSuccess("Admin logged out successfully");
    return true;
  };

  const value = {
    admin,
    adminToken,
    adminRole,

    isAuthenticated: !!adminToken,
    isAdmin: adminRole === "super_admin",
    isManager: adminRole === "branch_manager",
    isStaff: adminRole === "staff",

    apiLoading,
    loading,

    requestAdminOtp,
    resendAdminOtp,
    verifyAdminOtp,
    logoutAdmin,
    setAdmin,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => useContext(AdminAuthContext);
